import React, { useState, useEffect } from 'react';
import { db, isConfigured } from '../firebaseConfig';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import DefaultImage from '../images/gitpp.jpg';

function About() {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({
    yearsOfExperience: 0,
    completedProjects: 0,
    inProgressProjects: 0
  });
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfileAndStats = async () => {
      if (!isConfigured) {
        setProfile(getDefaultProfile());
        calculateDefaultStats();
        setLoading(false);
        return;
      }

      try {
        // Fetch profile
        const docRef = doc(db, 'profile', 'main');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const profileData = docSnap.data();
          setProfile(profileData);
          await fetchStats(profileData.startYear || 2021);
        } else {
          setProfile(getDefaultProfile());
          calculateDefaultStats();
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(getDefaultProfile());
        calculateDefaultStats();
      } finally {
        setLoading(false);
      }
    };

    fetchProfileAndStats();
  }, []);

  // ✅ Trigger animation only after loading finishes
  useEffect(() => {
    if (!loading) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        },
        { threshold: 0.1 }
      );

      const aboutSection = document.getElementById('about');
      if (aboutSection) observer.observe(aboutSection);

      return () => {
        if (aboutSection) observer.unobserve(aboutSection);
      };
    }
  }, [loading]); // <-- re-runs when loading changes

  const fetchStats = async (startYear) => {
    try {
      const completedQuery = query(collection(db, 'projects'), where('status', '==', 'Completed'));
      const completedSnapshot = await getDocs(completedQuery);

      const inProgressQuery = query(collection(db, 'projects'), where('status', '==', 'In Progress'));
      const inProgressSnapshot = await getDocs(inProgressQuery);

      const currentYear = new Date().getFullYear();
      const yearsOfExperience = currentYear - startYear;

      setStats({
        yearsOfExperience,
        completedProjects: completedSnapshot.size,
        inProgressProjects: inProgressSnapshot.size
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      calculateDefaultStats();
    }
  };

  const calculateDefaultStats = () => {
    const currentYear = new Date().getFullYear();
    const startYear = 2021;
    const yearsOfExperience = currentYear - startYear;

    setStats({
      yearsOfExperience,
      completedProjects: yearsOfExperience,
      inProgressProjects: yearsOfExperience
    });
  };

  const getDefaultProfile = () => ({
    name: 'Drusilla Khulwael',
    bio: "I'm passionate about building modern and scalable software solutions. I specialize in frontend and backend development, crafting seamless user experiences, and optimizing performance. I enjoy solving complex problems and continuously learning new technologies to enhance my skill set.",
    profileImage: DefaultImage,
    cvUrl: 'Drusilla.pdf',
    startYear: 2021
  });

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-white text-xl animate-pulse">Loading...</div>
      </div>
    );
  }

  const displayProfile = profile || getDefaultProfile();
  const imageUrl = displayProfile.profileImage || DefaultImage;
  const cvUrl = displayProfile.cvUrl || 'Drusilla.pdf';

  return (
    <div
      className="min-h-[50vh] flex flex-col lg:flex-row items-center justify-center gap-8 sm:gap-12 lg:gap-16 px-4 sm:px-6 lg:px-8 py-8"
      id="about"
    >
      {/* Text Content */}
      <div
        className={`w-full lg:w-1/2 text-white flex flex-col gap-6 sm:gap-8 transition-all duration-1000 ease-out ${
          isVisible
            ? 'transform translate-x-0 opacity-100'
            : 'transform -translate-x-full opacity-0'
        }`}
      >
        <h2 className="font-bold text-xl sm:text-2xl md:text-3xl lg:text-4xl">
          ABOUT ME
        </h2>

        <p className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed">
          {displayProfile.bio}
        </p>

        <button className="w-fit px-6 py-3 bg-white text-gray-800 rounded-xl hover:bg-opacity-90 transition-colors duration-200 text-sm sm:text-base font-medium shadow-lg">
          <a href={cvUrl} download={`${displayProfile.name}_Resume.pdf`}>
            Download my CV
          </a>
        </button>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <div className="px-4 py-4 bg-white text-gray-800 rounded-xl text-center shadow-lg">
            <h4 className="font-bold text-xs sm:text-sm mb-2 text-gray-600">
              Years of Experience
            </h4>
            <p className="text-2xl sm:text-3xl font-bold text-[#FFB7CE]">
              {stats.yearsOfExperience}+
            </p>
          </div>
          <div className="px-4 py-4 bg-white text-gray-800 rounded-xl text-center shadow-lg">
            <h4 className="font-bold text-xs sm:text-sm mb-2 text-gray-600">
              Completed Projects
            </h4>
            <p className="text-2xl sm:text-3xl font-bold text-green-500">
              {stats.completedProjects}
            </p>
          </div>
          <div className="px-4 py-4 bg-white text-gray-800 rounded-xl text-center shadow-lg">
            <h4 className="font-bold text-xs sm:text-sm mb-2 text-gray-600">
              In Progress Projects
            </h4>
            <p className="text-2xl sm:text-3xl font-bold text-yellow-500">
              {stats.inProgressProjects}
            </p>
          </div>
        </div>
      </div>

      {/* Image */}
      <div
        className={`w-full sm:w-3/4 md:w-1/2 lg:w-1/2 max-w-md lg:max-w-none transition-all duration-1000 ease-out ${
          isVisible
            ? 'transform translate-x-0 opacity-100'
            : 'transform translate-x-full opacity-0'
        }`}
      >
        <img
          src={imageUrl}
          alt={`${displayProfile.name} | Software Developer`}
          className="rounded-2xl w-full h-[90vh] object-cover shadow-2xl"
          onError={(e) => {
            e.target.src = DefaultImage;
          }}
        />
      </div>
    </div>
  );
}

export default About;
